import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config`);
      setConfig(res.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async (key, value) => {
    setSaving(true);
    try {
      await axios.put(`${API}/config`, { key, value: String(value) });
      toast.success(`Updated ${key}`);
      setConfig(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      toast.error(`Failed to update ${key}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-mono font-medium text-white">Settings</h1>
        <p className="text-sm text-zinc-500">Configure SEO Autopilot behavior</p>
      </div>

      {/* Scheduler Settings */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300">Scheduler Settings</CardTitle>
          <CardDescription className="text-zinc-500">Configure batch size and timing</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Batch Size</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={config.batch_size || '5'}
                  onChange={(e) => handleChange('batch_size', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="batch-size-input"
                />
                <Button 
                  size="sm"
                  onClick={() => handleSave('batch_size', config.batch_size)}
                  disabled={saving}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  <Save className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-zinc-600">Number of items to process per run</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Schedule Interval (minutes)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="5"
                  max="1440"
                  value={config.schedule_interval_minutes || '30'}
                  onChange={(e) => handleChange('schedule_interval_minutes', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="interval-input"
                />
                <Button 
                  size="sm"
                  onClick={() => handleSave('schedule_interval_minutes', config.schedule_interval_minutes)}
                  disabled={saving}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  <Save className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-zinc-600">Time between scheduled runs</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LLM Settings */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300">LLM Provider Settings</CardTitle>
          <CardDescription className="text-zinc-500">Configure AI content generation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Provider</Label>
              <div className="flex gap-2">
                <Select 
                  value={config.llm_provider || 'emergent'} 
                  onValueChange={(v) => { handleChange('llm_provider', v); handleSave('llm_provider', v); }}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="emergent">Emergent (OpenAI)</SelectItem>
                    <SelectItem value="ollama">Ollama (Self-hosted)</SelectItem>
                    <SelectItem value="mock">Mock (Testing)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-zinc-600">Ollama falls back to Emergent if unavailable</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Model</Label>
              <div className="flex gap-2">
                <Select 
                  value={config.llm_model || 'gpt-4o-mini'} 
                  onValueChange={(v) => { handleChange('llm_model', v); handleSave('llm_model', v); }}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="model-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    <SelectItem value="gpt-5.2">GPT-5.2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-zinc-600">OpenAI model for content generation</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* QA Settings */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300">QA Gate Settings</CardTitle>
          <CardDescription className="text-zinc-500">Minimum word counts for each page type</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Blog Min Words</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="100"
                  max="5000"
                  value={config.min_word_count_blog || '800'}
                  onChange={(e) => handleChange('min_word_count_blog', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="blog-words-input"
                />
                <Button 
                  size="sm"
                  onClick={() => handleSave('min_word_count_blog', config.min_word_count_blog)}
                  disabled={saving}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  <Save className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Directory Min Words</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="100"
                  max="5000"
                  value={config.min_word_count_directory || '500'}
                  onChange={(e) => handleChange('min_word_count_directory', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="directory-words-input"
                />
                <Button 
                  size="sm"
                  onClick={() => handleSave('min_word_count_directory', config.min_word_count_directory)}
                  disabled={saving}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  <Save className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Event Min Words</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="100"
                  max="5000"
                  value={config.min_word_count_event || '600'}
                  onChange={(e) => handleChange('min_word_count_event', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="event-words-input"
                />
                <Button 
                  size="sm"
                  onClick={() => handleSave('min_word_count_event', config.min_word_count_event)}
                  disabled={saving}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  <Save className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Internal Linking */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-sm font-mono text-zinc-300">Internal Linking</CardTitle>
          <CardDescription className="text-zinc-500">Configure internal link insertion</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Min Links Per Page</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={config.min_internal_links || '3'}
                  onChange={(e) => handleChange('min_internal_links', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="min-links-input"
                />
                <Button 
                  size="sm"
                  onClick={() => handleSave('min_internal_links', config.min_internal_links)}
                  disabled={saving}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  <Save className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Max Links Per Page</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={config.max_internal_links || '8'}
                  onChange={(e) => handleChange('max_internal_links', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="max-links-input"
                />
                <Button 
                  size="sm"
                  onClick={() => handleSave('max_internal_links', config.max_internal_links)}
                  disabled={saving}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  <Save className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
